"use client";

import { motion } from "motion/react";
import { useState } from "react";

import { ownerStore } from "@up/data";

import { Check } from "./check-animated";

import type { TContact } from "@up/data";
import type { PropsWithChildren, ReactNode } from "react";

type ButtonProps = PropsWithChildren<{
  disabled?: boolean;
  onClick: () => void;
}>;

function Button({ children, disabled = false, onClick }: ButtonProps) {
  const [selected, setSelected] = useState(false);

  return (
    <button
      className={`outline-3 flex w-full cursor-pointer rounded-lg border border-solid outline outline-offset-0 outline-transparent transition-all duration-300 focus:outline-offset-2 focus:outline-gray-200 ${
        selected
          ? "border-green-600 outline-0 outline-emerald-200"
          : "border-gray-400"
      } bg-transparent px-4 py-3 text-left`}
      data-testid="profile-select-button"
      onClick={() => {
        setSelected(true);
      }}
    >
      <div className="flex flex-grow flex-col justify-center">{children}</div>
      {selected ? (
        <div className="animation-check-circle">
          <Check animationCallback={onClick} />
        </div>
      ) : (
        <div style={{ width: "50px", height: "50px" }} />
      )}
    </button>
  );
}

type Props = {
  heading: ReactNode;
  options?: Array<{
    children: ReactNode;
    onClick: () => void;
  }>;
  contacts?: Array<TContact>;
  select?: (email: string, contactId: string) => void;
};
export function ProfileSelect({
  heading,
  options = [],
  contacts = [],
  select,
}: Props) {
  const [animateView, setAnimateView] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<
    undefined | { email: string; contactId: string }
  >(undefined);

  const makeSelection = async function (contact: TContact) {
    ownerStore.set({
      email: contact.profile.email,
      contactId: contact._id,
    });
    setSelectedProfile({
      email: contact.profile.email,
      contactId: contact._id,
    });
    setAnimateView(true);
  };

  return (
    <motion.div
      animate={animateView ? "out" : "in"}
      variants={{
        in: { opacity: 1, y: 0 },
        out: { opacity: 0, y: "25px" },
      }}
      transition={{
        duration: 0.5,
        ease: [0.17, 0.55, 0.55, 1],
      }}
      onAnimationComplete={() => {
        if (selectedProfile && select) {
          select(selectedProfile.email, selectedProfile.contactId);
        }
      }}
      style={{ maxWidth: "600px" }}
    >
      {heading}
      <ul className="m-0 list-none p-0" data-testid="profile-select">
        {contacts.map((contact) => {
          return (
            <li className="mb-3" key={contact.profile.email}>
              <Button
                onClick={() => {
                  makeSelection(contact);
                }}
              >
                <>
                  <div
                    className="font-medium"
                    data-testid="profile-select-name"
                  >
                    {contact.profile.firstName ?? null}{" "}
                    {contact.profile.lastName ?? null}
                  </div>
                  <div
                    className="opacity-70"
                    data-testid="profile-select-email"
                  >
                    {contact.profile.email}
                  </div>
                </>
              </Button>
            </li>
          );
        })}
        {options.map((option, index) => {
          return (
            <li className="mb-3" key={index}>
              <Button onClick={option.onClick}>{option.children}</Button>
            </li>
          );
        })}
      </ul>
    </motion.div>
  );
}
